// One BroadcastChannel for the whole studio (TODO §12.5.3).
//
// Several pages (or Electron windows, §14) hold different decks open at once and must not
// silently diverge. Everything cross-page rides this one channel:
//
//   deck-updated  — a deck record was written. A read-only viewer re-hydrates; the
//                   documents home refreshes its list.
//   deck-deleted  — a deck was cascade-deleted. Any page holding it shows "not found".
//   deck-created  — a deck was added (new / duplicate / import). List refresh only.
//   lock-request  — a read-only page is asking the current writer to hand over (§12.5.2).
//   lock-released — the writer let go; whoever asked may now take the lock.
//   nugget-updated — a knowledge nugget was written (§17.2.4). A page holding a deck that
//                   links it re-materializes that slide live, which is what makes two
//                   windows on two decks look like one document.
//   nugget-deleted — a nugget was removed from the shelf (§17.6.1). Only the nugget PANEL
//                   reacts; decks deliberately do not, keeping the slide and dropping the
//                   dead link on their next open.
//
// BroadcastChannel never delivers a page its own posts, which is exactly what we want:
// the writer must not react to its own `deck-updated`.
//
// It is absent in a few contexts (older Safari, some embedded webviews). Like IndexedDB
// (§4.5) that degrades rather than throws: posts become no-ops and subscribers simply never
// fire, leaving each page correct on its own and stale only with respect to the others.

export type DeckMessage =
  | { type: "deck-updated"; deckId: string; rev: number }
  | { type: "deck-deleted"; deckId: string }
  | { type: "deck-created"; deckId: string }
  | { type: "lock-request"; deckId: string; from: string }
  // `to` is the requesting page's id, or "*" when the writer is going away entirely
  // (page unload) and any waiting page may grab the lock.
  | { type: "lock-released"; deckId: string; to: string }
  // §17.2.4 Additive, and keyed by nuggetId rather than deckId — which is the point: the
  // pages that care are the ones holding some OTHER deck that links this nugget.
  | { type: "nugget-updated"; nuggetId: string; rev: number }
  | { type: "nugget-deleted"; nuggetId: string };

const CHANNEL_NAME = "slidecraft";

// Identifies this page in lock hand-offs. Not persisted — a reload is a different page.
export const PAGE_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : String(Math.random());

const listeners = new Set<(msg: DeckMessage) => void>();

// `undefined` = not opened yet, `null` = unavailable here.
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === "undefined") {
    channel = null;
    return null;
  }
  try {
    const opened = new BroadcastChannel(CHANNEL_NAME);
    opened.onmessage = (event: MessageEvent<DeckMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg.type !== "string") return;
      // One listener throwing must not starve the rest.
      for (const fn of [...listeners]) {
        try {
          fn(msg);
        } catch (err) {
          console.warn("[deck-channel] listener failed", err);
        }
      }
    };
    channel = opened;
  } catch (err) {
    console.warn("[deck-channel] unavailable; pages will not sync", err);
    channel = null;
  }
  return channel;
}

export function postDeckMessage(msg: DeckMessage): void {
  try {
    getChannel()?.postMessage(msg);
  } catch (err) {
    // Firing-and-forgetting: a failed broadcast costs freshness in other pages, never
    // correctness in this one.
    console.warn("[deck-channel] post failed", err);
  }
}

// Subscribe to every message; returns the unsubscribe. Opening the channel lazily on first
// subscribe (rather than at import) keeps a page that never touches decks from holding one.
export function subscribeDeckMessages(
  fn: (msg: DeckMessage) => void,
): () => void {
  getChannel();
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
